import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Finding, MasterDataRecordDto, MasterDataSet } from '@complan/contracts';
import { Button } from '@/components/ui/button';
import { DateField } from '@/shared/components/DateField';
import { EditorDialog, EditorDialogActions } from '@/shared/components/EditorDialog';
import { Field, FORM_CONTROL_CLASS, TextInput } from '@/features/initiative/intake/fields';
import { cn } from '@/lib/utils';
import { PlatformColourPicker } from './PlatformColourPicker';
import { FindingsAlert } from './FindingsAlert';
import type { MasterDataWritePayload } from './admin-data';

/**
 * The master data record editor (task 23.6, R16.1-R16.3, R16.8, R16.11, R16.12).
 *
 * One dialog serves create and edit for every set, building the per-set write
 * payload: the shared shape (code, name, display order, effective-from, active)
 * plus the set's extras — a Platform's colour, a priority's
 * Elevated_Priority_Flag, a milestone type's mandatory rule, Calendar_Relevance_Flag
 * and preceding type. It submits the payload and, on a `validation_failed`,
 * renders the findings the server returned (a duplicate active name R16.11, a
 * Platform colour violation R16.12) — it never decides a rule itself.
 */

/** The per-set extra fields the editor tracks alongside the shared shape. */
interface EditorState {
  code: string;
  name: string;
  displayOrder: string;
  effectiveFrom: string;
  isActive: boolean;
  colour: string;
  elevatedPriorityFlag: boolean;
  calendarRelevanceFlag: boolean;
  isMandatory: boolean;
  precedingMilestoneTypeId: string;
  posCount: string;
  sourceTimestamp: string;
}

/** A milestone-type option the preceding-type select offers. */
export interface MilestoneTypeOption {
  readonly id: string;
  readonly name: string;
}

export interface RecordEditorDialogProps {
  readonly set: MasterDataSet;
  /** The record being edited, or `null` to create a new one. */
  readonly record: MasterDataRecordDto | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Other milestone types, offered as the preceding-type choice (milestone-type set only). */
  readonly milestoneTypeOptions: readonly MilestoneTypeOption[];
  readonly onSubmit: (payload: MasterDataWritePayload) => void;
  readonly isSubmitting: boolean;
  /** Findings the server returned for the last rejected submit. */
  readonly findings: readonly Finding[];
  /**
   * Restores keyboard focus to the row or toolbar button that opened this dialog
   * (R10.13's general focus-restore expectation, R18.6). This dialog is a
   * controlled `Dialog` with no `DialogTrigger` in its tree — Radix's own
   * `triggerRef`-based restore only fires for an in-tree `DialogTrigger`, so
   * without this the closing focus would fall back to `<body>` instead of the
   * button that opened the dialog.
   */
  readonly onCloseAutoFocus?: (event: Event) => void;
}

const TODAY = (): string => new Date().toISOString().slice(0, 10);

function initialState(record: MasterDataRecordDto | null): EditorState {
  return {
    code: record?.code ?? '',
    name: record?.name ?? '',
    displayOrder: String(record?.displayOrder ?? 0),
    effectiveFrom: record?.effectiveFrom ?? TODAY(),
    isActive: record?.isActive ?? true,
    colour: record?.colour ?? '#2F6DB5',
    elevatedPriorityFlag: record?.elevatedPriorityFlag ?? false,
    calendarRelevanceFlag: record?.calendarRelevanceFlag ?? false,
    isMandatory: record?.isMandatory ?? false,
    precedingMilestoneTypeId: record?.precedingMilestoneTypeId ?? '',
    posCount: String(record?.posCount ?? 0),
    sourceTimestamp: record?.sourceTimestamp?.slice(0, 10) ?? '',
  };
}

export function RecordEditorDialog({
  set,
  record,
  open,
  onOpenChange,
  milestoneTypeOptions,
  onSubmit,
  isSubmitting,
  findings,
  onCloseAutoFocus,
}: RecordEditorDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const isEdit = record !== null;
  const [state, setState] = useState<EditorState>(() => initialState(record));

  // Re-seed the form whenever the target record (or create/edit mode) changes.
  const seedKey = `${set}:${record?.id ?? 'new'}:${String(open)}`;
  const [lastSeed, setLastSeed] = useState(seedKey);
  if (seedKey !== lastSeed) {
    setLastSeed(seedKey);
    setState(initialState(record));
  }

  const setLabel = t(`admin.set.${set}`);

  const payload = useMemo<MasterDataWritePayload>(() => {
    const shared = {
      code: state.code,
      name: state.name,
      displayOrder: Number(state.displayOrder) || 0,
      effectiveFrom: state.effectiveFrom as MasterDataWritePayload['effectiveFrom'],
      isActive: state.isActive,
    };
    if (set === 'platform') {
      return { ...shared, colour: state.colour };
    }
    if (set === 'priority') {
      return { ...shared, elevatedPriorityFlag: state.elevatedPriorityFlag };
    }
    if (set === 'milestone-type') {
      return {
        ...shared,
        calendarRelevanceFlag: state.calendarRelevanceFlag,
        isMandatory: state.isMandatory,
        precedingMilestoneTypeId:
          state.precedingMilestoneTypeId === '' ? null : state.precedingMilestoneTypeId,
      };
    }
    if (set === 'pos-segment') {
      return {
        ...shared,
        posCount: Number(state.posCount) || 0,
        sourceTimestamp: state.sourceTimestamp === '' ? null : state.sourceTimestamp,
      };
    }
    return shared;
  }, [set, state]);

  const controlClass = cn(FORM_CONTROL_CLASS, 'w-full');

  return (
    <EditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        isEdit
          ? t('admin.masterData.editor.editTitle', { set: setLabel })
          : t('admin.masterData.editor.createTitle', { set: setLabel })
      }
      {...(onCloseAutoFocus ? { onCloseAutoFocus } : {})}
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(payload);
        }}
      >
        <FindingsAlert findings={findings} />

        {/*
          Two fields per row wherever a field does not need the full width, at the
          `gap-3` of the Manpower editors (UI spec §3.11). Code and name take the
          whole row because a 100-character name in a half-width cell reads as a
          truncated one; display order and effective-from pair as the two values
          that decide where and when the record appears.

          `mb-0` on every field is load-bearing: `Field` ships `mb-3` of its own,
          and with the grid's `gap-y-3` that spacing applied twice.
        */}
        <div className="grid grid-cols-2 items-start gap-x-3 gap-y-3">
          <Field
            label={t('admin.masterData.editor.code')}
            htmlFor="md-code"
            required
            requiredMark={t('admin.common.required')}
            className="col-span-2 mb-0"
          >
            <TextInput
              id="md-code"
              className="w-full"
              value={state.code}
              maxLength={20}
              onChange={(event) => setState((s) => ({ ...s, code: event.target.value }))}
            />
          </Field>

          <Field
            label={t('admin.masterData.editor.name')}
            htmlFor="md-name"
            required
            requiredMark={t('admin.common.required')}
            className="col-span-2 mb-0"
          >
            <TextInput
              id="md-name"
              className="w-full"
              value={state.name}
              maxLength={100}
              onChange={(event) => setState((s) => ({ ...s, name: event.target.value }))}
            />
          </Field>

          <Field
            label={t('admin.masterData.editor.displayOrder')}
            htmlFor="md-order"
            className="mb-0"
          >
            <TextInput
              id="md-order"
              type="number"
              min={0}
              className="w-full"
              value={state.displayOrder}
              onChange={(event) => setState((s) => ({ ...s, displayOrder: event.target.value }))}
            />
          </Field>

          <Field
            label={t('admin.masterData.editor.effectiveFrom')}
            htmlFor="md-effective"
            className="mb-0"
          >
            <DateField
              value={state.effectiveFrom}
              onChange={(date) => setState((s) => ({ ...s, effectiveFrom: date ?? '' }))}
              label={t('admin.masterData.editor.effectiveFrom')}
            />
          </Field>

          {set === 'platform' ? (
            <div className="col-span-2">
              <PlatformColourPicker
                value={state.colour}
                onChange={(colour) => setState((s) => ({ ...s, colour }))}
              />
            </div>
          ) : null}

          {set === 'priority' ? (
            <label className="col-span-2 flex items-center gap-2 text-[12px] text-txt">
              <input
                type="checkbox"
                checked={state.elevatedPriorityFlag}
                onChange={(event) =>
                  setState((s) => ({ ...s, elevatedPriorityFlag: event.target.checked }))
                }
              />
              <span>{t('admin.masterData.editor.elevatedPriorityFlag')}</span>
            </label>
          ) : null}

          {set === 'milestone-type' ? (
            <>
              <label className="col-span-2 flex items-center gap-2 text-[12px] text-txt">
                <input
                  type="checkbox"
                  checked={state.isMandatory}
                  onChange={(event) => setState((s) => ({ ...s, isMandatory: event.target.checked }))}
                />
                <span>{t('admin.masterData.editor.isMandatory')}</span>
              </label>
              <label className="col-span-2 flex items-center gap-2 text-[12px] text-txt">
                <input
                  type="checkbox"
                  checked={state.calendarRelevanceFlag}
                  onChange={(event) =>
                    setState((s) => ({ ...s, calendarRelevanceFlag: event.target.checked }))
                  }
                />
                <span>{t('admin.masterData.editor.calendarRelevanceFlag')}</span>
              </label>
              <Field
                label={t('admin.masterData.editor.precedingMilestoneType')}
                htmlFor="md-preceding"
                className="col-span-2 mb-0"
              >
                <select
                  id="md-preceding"
                  className={controlClass}
                  value={state.precedingMilestoneTypeId}
                  onChange={(event) =>
                    setState((s) => ({ ...s, precedingMilestoneTypeId: event.target.value }))
                  }
                >
                  <option value="">{t('admin.masterData.editor.precedingNone')}</option>
                  {milestoneTypeOptions
                    .filter((option) => option.id !== record?.id)
                    .map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                </select>
              </Field>
            </>
          ) : null}

          {set === 'pos-segment' ? (
            <>
              <Field
                label={t('admin.masterData.editor.posCount')}
                htmlFor="md-pos-count"
                className="mb-0"
                hint={t('admin.masterData.editor.posCountHint')}
              >
                <TextInput
                  id="md-pos-count"
                  type="number"
                  min={0}
                  className="w-full"
                  value={state.posCount}
                  onChange={(event) => setState((s) => ({ ...s, posCount: event.target.value }))}
                />
              </Field>
              <Field
                label={t('admin.masterData.editor.sourceTimestamp')}
                htmlFor="md-source-timestamp"
                className="mb-0"
                hint={t('admin.masterData.editor.sourceTimestampHint')}
              >
                <DateField
                  value={state.sourceTimestamp}
                  onChange={(date) => setState((s) => ({ ...s, sourceTimestamp: date ?? '' }))}
                  label={t('admin.masterData.editor.sourceTimestamp')}
                />
              </Field>
            </>
          ) : null}

          {/* The active flag reads as one field with its hint, so it takes the
              `Field` gap rather than a bare label plus a floating paragraph. */}
          <div className="col-span-2 flex flex-col gap-1">
            <label className="flex items-center gap-2 text-[12px] text-txt">
              <input
                type="checkbox"
                checked={state.isActive}
                onChange={(event) => setState((s) => ({ ...s, isActive: event.target.checked }))}
              />
              <span>{t('admin.masterData.editor.isActive')}</span>
            </label>
            <p className="text-muted-foreground text-[11px]">
              {t('admin.masterData.editor.isActiveHint')}
            </p>
          </div>
        </div>

        <EditorDialogActions>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('admin.common.saving') : t('admin.common.save')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t('admin.common.cancel')}
          </Button>
        </EditorDialogActions>
      </form>
    </EditorDialog>
  );
}
